// 1. React and built-in hooks
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';

// 2. Local imports
import { Accordion } from './ui/Accordion';
import { DeleteHistoryModal } from './DeleteHistoryModal';
import { ChatHistoryItem, ChatHistoryItems } from '../types';
import { ChatbotContext } from '../contexts/ChatbotContext';

interface ChatHistoryPanelProps {
  showChatHistoryPanel: boolean;
  setShowChatHistoryPanel: (show: boolean) => void;
  chatHistoryList: ChatHistoryItems[];
  isPromptGenerating: boolean;
}

const ChatHistoryPanelMemo: React.FC<ChatHistoryPanelProps> = ({
  showChatHistoryPanel,
  setShowChatHistoryPanel,
  chatHistoryList,
  isPromptGenerating,
}) => {
  const { feedbackSubmit } = useContext(ChatbotContext);
  const [inputValue, setInputValue] = useState<string>('');
  const [accordionItems, setAccordionItems] = useState<ChatHistoryItems[]>([]);
  const [showDeleteHistoryModal, setShowDeleteHistoryModal] = useState<boolean>(false);
  const [selectedChatHistoryItem, setSelectedChatHistoryItem] = useState<ChatHistoryItem>({
    ConfigurationName: '',
    blobURL: '',
    displayName: '',
    fileName: '',
    genArcPromptAnalyticsUID: '',
    promptName: '',
  });

  useEffect(() => {
    setAccordionItems(chatHistoryList);
  }, [chatHistoryList]);

  const handleClose = () => setShowChatHistoryPanel(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const filteredAccordionItems = useMemo(() => {
    return inputValue
      ? accordionItems
          .map((item: ChatHistoryItems) => ({
            ...item,
            chatHistoryItems: (item.chatHistoryItems ?? []).filter((chat: ChatHistoryItem) =>
              (chat.displayName ?? '').toLowerCase().includes(inputValue.toLowerCase())
            ),
          }))
          .filter((item: ChatHistoryItems) => (item.chatHistoryItems ?? []).length > 0)
      : accordionItems;
  }, [inputValue, accordionItems]);

  const showAllAccordionContent = useMemo(() => {
    return inputValue ? true : false;
  }, [inputValue]);

  return (
    <>
      <DeleteHistoryModal
        showDeleteHistoryModal={showDeleteHistoryModal}
        setShowDeleteHistoryModal={setShowDeleteHistoryModal}
        selectedChatHistoryItem={selectedChatHistoryItem}
      />
      <div
        className={`flex h-full min-w-[304px] w-[304px] transition-all duration-[500ms] ease-[ease-out] tb:hidden ${
          showChatHistoryPanel ? '' : '!min-w-0 !w-0 overflow-hidden'
        }`}
      >
        <div className="chat-history h-full w-full">
          <div className="relative flex flex-col h-full bg-gamma-panel-bg font-graphik text-black">
            {(isPromptGenerating || feedbackSubmit) && (
              <div className="bg-[#f1f1f180] h-full w-full absolute z-[1]"></div>
            )}
            <div className="flex flex-row justify-between pl-4 pr-6 pt-5 pb-6 text-base">
              <span>Chat history</span>
              <img
                src="/icons/Close.svg"
                alt="Send suggested prompt"
                className="w-[16px] h-[16px] cursor-pointer"
                onClick={handleClose}
              />
            </div>
            <div className="ml-4 mr-4 mb-[22px] flex relative gap-3">
              <img className="absolute h-3 w-3 left-[11px] top-2" src="/icons/icon-search.svg" alt="search icon" />
              <input
                type="text"
                className="chat-history-input pl-[31px] pr-[20px] py-2 h-7 text-xs rounded outline-none flex-1"
                name="myInput"
                placeholder="Search by title"
                value={inputValue}
                onChange={handleInputChange}
              />
              <img
                src="icons/Close.svg"
                alt="Clear"
                className=" absolute right-4 top-1/2 transform -translate-y-1/2 bg-transparent  w-4 h-4 cursor-pointer"
                onClick={() => setInputValue('')}
              />
            </div>
            <div className="px-4 text-xs h-full Scrollbar">
              <Accordion
                accordionItems={filteredAccordionItems}
                setSelectedChatHistoryItem={setSelectedChatHistoryItem}
                setShowDeleteHistoryModal={setShowDeleteHistoryModal}
                showAllAccordionContent={showAllAccordionContent}
              />
            </div>
            {/* <div className="px-4 pb-[40px]">
              <button
                type="button"
                className="w-full text-xs flex justify-between items-center h-9 border text-[#6B12B9] pl-6 pr-[18px] py-0 rounded-[7px] border-solid border-[#6B12B9] bg-white"
              >
                <span>New Chat</span>
                <img src="/icons/icon-plus.svg" alt="icon-plus" />
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export const ChatHistoryPanel = memo(ChatHistoryPanelMemo);
